// Control.js
import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { formatBalance, balanceToneClass, balanceChipClass } from '../../../lib/helpers/balance';
import { UserContext } from "../../../context/UserContext";
import AddCashModal from "../../Elements/AddCashModal";
import NotaSocioPopup, { notaConAviso, tieneAvisoSocio } from "../../Elements/NotaSocioPopup";
import { useNavigate, useLocation } from "react-router-dom";
import CustomerModal from "../CustomerComponents/CustomerModal";
import ClientDetailModal from "../CustomerComponents/ClientDetailModal";
import { filtroBusqueda, getImageUrl, socioNum } from "../../../lib/helpers";
import MemberPhoto, { FOTO_DE_FILA } from "../../Elements/MemberPhoto";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { MdCreditCard, MdLocalDrink } from "react-icons/md";
import { GrLogout } from "react-icons/gr";
import { GiHemp } from "react-icons/gi";
import { HiOutlineShoppingCart } from "react-icons/hi";
import { Search, SearchX, EllipsisVertical, CircleUserRound, LogIn } from "lucide-react";
import SectionCounter from "../../Elements/SectionCounter";
import Button from "../../Elements/Button";
import MenuFlotante from "../../Elements/MenuFlotante";
import CustomModal from "../../Elements/CustomModal";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import axios from 'axios';
import CaducadoChip from "../../Elements/CaducadoChip";
import useConfirmar from "../../Elements/useConfirmar";
import { useInVenueUsers } from "../../../context/useInVenueUsers";
import Dispenser from "../DispenserComponents/Dispenser";
import EnviarABarra from "../BarraComponents/EnviarABarra";
import { VENUE_TYPES } from "../../../lib/consts/venueTypes";
import "./Control.css";

// ─── helpers ─────────────────────────────────────────────────────────────────

function calcElapsedMins(entryTime) {
  if (!entryTime) return 0;
  const t =
    typeof entryTime === "string"
      ? new Date(entryTime.endsWith("Z") ? entryTime : entryTime + "Z")
      : new Date(entryTime);
  if (isNaN(t.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - t.getTime()) / 60000));
}

function ElapsedCell({ entryTime }) {
  const [mins, setMins] = useState(() => calcElapsedMins(entryTime));
  useEffect(() => {
    const id = setInterval(() => setMins(calcElapsedMins(entryTime)), 15000);
    return () => clearInterval(id);
  }, [entryTime]);
  return <span className="text-xs text-[var(--foreground)]">{mins} min</span>;
}

/*
 * Ítem del menú de acciones de la fila. Es el `Club_Menu_button` del sistema —el
 * mismo del sidebar— así que el resaltado es su Hover: `sidebar-hover`, el slate
 * claro. Nada de `lime-400`, que era de la paleta anterior y ya no existe.
 *
 * OJO al leer el frame (3527:27151): allí «Saldo» sale en oscuro porque está en
 * estado ACTIVE, que es el de «esta es la sección en la que estás». En un menú de
 * destinos no hay ninguno actual, así que lo que aplica es el Hover.
 */
const itemMenu =
  "group flex h-8 w-full items-center gap-2.5 rounded-sm px-3 text-sm " +
  "text-[var(--sidebar-foreground)] transition-colors hover:bg-[var(--sidebar-hover)]";

function ExitStatusCell({ entryTime }) {
  const [mins, setMins] = useState(() => calcElapsedMins(entryTime));
  useEffect(() => {
    const id = setInterval(() => setMins(calcElapsedMins(entryTime)), 15000);
    return () => clearInterval(id);
  }, [entryTime]);
  const canLeave = mins >= 20;
  return canLeave ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-[var(--success)]">
      <span className="w-2 h-2 rounded-full bg-[var(--success)] flex-shrink-0" />
      Puede salir
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs text-[var(--foreground)]">
      <span className="w-2 h-2 rounded-full bg-[var(--destructive)] flex-shrink-0" />
      {"< 20 min"}
    </span>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function Control() {
  const [token, , , venueID, venueName, , , , , , , venueType] = useContext(UserContext);
  const [clients, setClients] = useState([]);
  const [id, setId] = useState(null);
  const [activeModal, setActiveModal] = useState(false);
  const [user_id, setUser_id] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [showNuevaEntradaModal, setShowNuevaEntradaModal] = useState(false);
  const [searchFieldNueva, setSearchFieldNueva] = useState("");
  const [nuevaResults, setNuevaResults] = useState([]);
  const [nuevaSearching, setNuevaSearching] = useState(false);
  const nuevaSearchTimer = useRef(null);
  const [sociosSearch, setSociosSearch] = useState("");
  const [draftTotalsByUser, setDraftTotalsByUser] = useState({});
  const [entriesPanelUserId, setEntriesPanelUserId] = useState(null);
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const { confirmar, dialogo: dialogoConfirmar } = useConfirmar();
  // El aviso de notas es OPT-IN POR SOCIO (profile.notes_popup, compartido
  // entre staff). El panel ya no se auto-abre nunca (secuestraba la
  // interacción — flujo de Iván); en su lugar, el socio marcado hace saltar
  // NotaSocioPopup al interactuar.
  const [avisoSocio, setAvisoSocio] = useState(null);
  const [notesPopupSaving, setNotesPopupSaving] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState("");
  // Menú ⋮ de fila: { user, anchorEl }. Lo posiciona MenuFlotante (portal, flip
  // con altura MEDIDA); antes iba a mano con una altura estimada de 300 px.
  const [actionMenu, setActionMenu] = useState(null);
  const isSocialClub = venueType === VENUE_TYPES.SOCIAL_CLUB;

  const navigate = useNavigate();
  const location = useLocation();
  const productosPanelRef = useRef(null);

  const {
    usersInVenue: usersShop,
    markUserEntered,
    markUserLeft,
    refresh: refreshUsersShop,
    updateUserCredit,
    updateUserImage,
    updateUser,
  } = useInVenueUsers(token, venueID);

  const entriesPanelSyncClient = useMemo(() => {
    if (!entriesPanelUserId) return null;
    return usersShop.find((u) => String(u.id) === String(entriesPanelUserId)) ?? null;
  }, [entriesPanelUserId, usersShop]);

  const clientToEdit = useMemo(() => {
    if (!id) return null;
    return (
      usersShop.find((u) => String(u.id) === String(id)) ??
      clients.find((u) => String(u.id) === String(id)) ??
      null
    );
  }, [id, usersShop, clients]);

  // "Perfil" abre la FICHA del socio (petición del equipo, 14-ago), no la
  // edición: antes saltaba directo al modal "Editar socio" y descolocaba.
  // La edición sigue a un toque: botón Editar dentro de la propia ficha.
  // Snapshot al abrir la ficha + derivación EN VIVO (fix review 16-ago): si el
  // saldo/las notas cambian en usersShop, la ficha abierta lo refleja al momento;
  // si el socio desaparece de las listas (checkout de un compañero), se conserva
  // el snapshot para no cerrarle el modal al staff en las narices.
  const [perfilSeed, setPerfilSeed] = useState(null);
  const perfilClient = useMemo(() => {
    if (!perfilSeed) return null;
    const live =
      usersShop.find((x) => String(x.id) === String(perfilSeed.id)) ||
      clients.find((x) => String(x.id) === String(perfilSeed.id));
    return live || perfilSeed;
  }, [perfilSeed, usersShop, clients]);
  const openSocioPerfil = useCallback((userId) => {
    const socio =
      usersShop.find((x) => String(x.id) === String(userId)) ||
      clients.find((x) => String(x.id) === String(userId));
    if (socio) setPerfilSeed(socio);
  }, [usersShop, clients]);

  const selectedClientName = entriesPanelSyncClient
    ? `${entriesPanelSyncClient.profile?.first_name || ""} ${entriesPanelSyncClient.profile?.last_name || ""}`.trim() || entriesPanelSyncClient.email
    : "";

  useEffect(() => {
    setNotesDraft(entriesPanelSyncClient?.profile?.notes || "");
    setNotesError("");
    setNotesSaving(false);
  }, [entriesPanelSyncClient?.id, entriesPanelSyncClient?.profile?.notes]);

  const selectUserForNotes = useCallback((userId, interact = false, socio = null) => {
    // Aviso solo al CAMBIAR de socio: re-taps del ya seleccionado no re-disparan
    // (una vez por interacción, la mecánica validada con Kong). El objeto `socio`
    // llega del embed del POS — un socio buscado sin check-in no está en usersShop.
    if (interact && String(userId) !== String(entriesPanelUserId)) {
      const u = socio || usersShop.find((x) => String(x.id) === String(userId));
      if (u && notaConAviso(u)) setAvisoSocio(u);
    }
    setEntriesPanelUserId(userId);
  }, [usersShop, entriesPanelUserId]);

  // Gesto de fila/tarjeta: vincula el socio al panel de notas y abre su ficha.
  //
  // `interact = false` A PROPÓSITO: aquí NO salta el aviso. Antes sí, y el popup
  // se pintaba por encima del modal de la ficha (z-index) — dos ventanas
  // apiladas en la tablet de la puerta.
  //
  // No se pierde ningún aviso al quitarlo, y el motivo es que los dos
  // disparadores nunca usaron el mismo predicado: el check-in va con
  // `tieneAvisoSocio` (nota O cumpleaños) y la fila iba con `notaConAviso`
  // (solo nota). La fila era un subconjunto estricto de la entrada — todo el que
  // saltaba aquí ya había saltado al entrar, y el de cumpleaños no saltaba nunca.
  //
  // Y para el socio que llega a la barra sin haber hecho check-in, la ficha
  // muestra la sección «Notas» siempre visible y sin plegar: la nota está ahí,
  // solo deja de interrumpir.
  //
  // Este comportamiento cambió tres veces en cuatro días (#457, #461, #468), así
  // que lo sujeta un test: EntradasGestoFila.wave5.test.jsx.
  const activateSocioRow = useCallback((userId) => {
    selectUserForNotes(userId, false);
    openSocioPerfil(userId);
  }, [selectUserForNotes, openSocioPerfil]);

  useEffect(() => {
    if (!entriesPanelUserId) return;
    if (!usersShop.some((u) => String(u.id) === String(entriesPanelUserId))) {
      setEntriesPanelUserId(null);
      setNotesPanelOpen(false);
    }
  }, [entriesPanelUserId, usersShop]);

  useEffect(() => {
    if (!isSocialClub) return;
    const uid = new URLSearchParams(location.search).get("userId");
    if (uid) setEntriesPanelUserId(uid);
  }, [isSocialClub, location.search]);

  const clearEntriesProductosPanel = useCallback(() => {
    setEntriesPanelUserId(null);
    const p = new URLSearchParams(location.search);
    if (!p.has("userId")) return;
    p.delete("userId");
    const qs = p.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const goToProductosOnControl = useCallback(
    (userId) => {
      const p = new URLSearchParams(location.search);
      p.set("panel", "productos");
      if (userId != null && userId !== "") p.set("userId", String(userId));
      navigate(`/control?${p.toString()}`, { replace: true });
    },
    [location.search, navigate]
  );

  useEffect(() => {
    if (!isSocialClub) return;
    if (new URLSearchParams(location.search).get("panel") !== "productos") return;
    const id = requestAnimationFrame(() => {
      productosPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [isSocialClub, location.search]);

  const openActionsMenu = (e, user) => {
    const boton = e.currentTarget;
    setActionMenu((prev) => (prev && prev.user.id === user.id ? null : { user, anchorEl: boton }));
  };
  const closeActionMenu = useCallback(() => setActionMenu(null), []);

  const getClients = useCallback(async () => {
    if (!venueID) return;
    try {
      const response = await axios.get("/v1/venue-users-for-entry", {
        params: { venue_id: venueID },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const list = response.data || [];
      setClients(list.filter((u) => (u.access_privileges || "").toLowerCase() !== "superadmin"));
    } catch (error) {
      console.error("❌ Error fetching clients:", error);
    }
  }, [token, venueID]);

  const searchNuevaEntrada = useCallback(async (term) => {
    const q = (term || "").trim();
    if (!q || !venueID) {
      setNuevaResults([]);
      setNuevaSearching(false);
      return;
    }
    try {
      const res = await axios.get(`/v1/clients/search/${venueID}`, {
        params: { q, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setNuevaResults(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("❌ Error searching socios:", error);
      setNuevaResults([]);
    } finally {
      setNuevaSearching(false);
    }
  }, [token, venueID]);

  const handleNuevaSearchChange = (e) => {
    const val = e.target.value;
    setSearchFieldNueva(val);
    clearTimeout(nuevaSearchTimer.current);
    if (!val.trim()) {
      setNuevaResults([]);
      setNuevaSearching(false);
      return;
    }
    setNuevaSearching(true);
    nuevaSearchTimer.current = setTimeout(() => searchNuevaEntrada(val), 350);
  };

  const resetNuevaEntradaSearch = useCallback(() => {
    clearTimeout(nuevaSearchTimer.current);
    setSearchFieldNueva("");
    setNuevaResults([]);
    setNuevaSearching(false);
  }, []);

  const afterVenueCreditBalanceChange = useCallback(
    async (updatedUserId, newBalance) => {
      const creditPatch =
        updatedUserId != null && newBalance !== undefined
          ? { userId: updatedUserId, balance: newBalance }
          : undefined;

      if (creditPatch) {
        updateUserCredit(updatedUserId, newBalance);
        setClients((prev) =>
          prev.map((c) => {
            if (String(c.id) !== String(updatedUserId)) return c;
            const balNum = Number.isFinite(Number(newBalance)) ? Number(newBalance) : 0;
            return {
              ...c,
              profile: {
                ...(c.profile || {}),
                credit_balance: balNum,
                cash_balance: balNum,
              },
            };
          })
        );
      }

      await Promise.all([refreshUsersShop({ creditPatch }), getClients()]);

      if (!isSocialClub || updatedUserId == null) return;
      if (String(entriesPanelUserId) === String(updatedUserId)) {
        clearEntriesProductosPanel();
      }
    },
    [isSocialClub, entriesPanelUserId, updateUserCredit, refreshUsersShop, getClients, clearEntriesProductosPanel]
  );

  const fetchDraftTotals = useCallback(async () => {
    if (!venueID || !token) return;
    try {
      const res = await axios.get("/v1/venue-draft-carts", {
        params: { venue_id: venueID },
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.draft_carts || [];
      const byUser = {};
      for (const row of list) {
        const uid = row.user_id;
        if (!byUser[uid]) byUser[uid] = 0;
        byUser[uid] += Number(row.total) || 0;
      }
      setDraftTotalsByUser(byUser);
    } catch (e) {
      console.warn("Fetch venue draft carts failed:", e);
    }
  }, [token, venueID]);

  useEffect(() => {
    fetchDraftTotals();
    const t = setInterval(fetchDraftTotals, 15000);
    return () => clearInterval(t);
  }, [fetchDraftTotals]);

  const goToProductosOnControlWithRefresh = useCallback(
    (userId) => {
      goToProductosOnControl(userId);
      fetchDraftTotals();
    },
    [goToProductosOnControl, fetchDraftTotals]
  );

  const handleCheckout = async (userId) => {
    const user = usersShop.find((u) => u.id === userId);
    if (!user) return;

    const totalOwed = draftTotalsByUser[userId];
    if (totalOwed != null && totalOwed > 0) {
      alert("Este socio tiene productos en el carrito. Finaliza la compra en Dispensario/Productos o en Carritos antes de sacarlo.");
      return;
    }

    if (user.entry_time) {
      const entryTime = new Date(user.entry_time);
      const now = new Date();
      const minutes = Math.floor((now - entryTime) / 60000);
      // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del
      // club y «Salir» moría sin decir nada (auditoría de UI, UI-003).
      if (20 - minutes > 0) {
        const sacar = await confirmar({
          title: "Sacar al socio",
          description: `Lleva solo ${minutes} min en la sala. ¿Registrar su salida igualmente?`,
          confirmLabel: "Registrar salida",
        });
        if (!sacar) return;
      }
    }

    try {
      await axios.patch("/v1/checkout", null, {
        params: { user_id: userId },
        headers: { Authorization: `Bearer ${token}` },
      });
      markUserLeft(userId);
    } catch (err) {
      console.error("Error checking out:", err);
    }
  };

  // El toggle "abrir al interactuar" se guarda EN EL SOCIO (profile.notes_popup)
  // con el mismo PUT que las notas — así lo comparten todos los compañeros.
  const socioPopupOn = !!entriesPanelSyncClient?.profile?.notes_popup;
  const toggleNotesPopup = useCallback(async () => {
    if (!entriesPanelSyncClient?.id || !token || notesPopupSaving) return;
    const next = !socioPopupOn;
    setNotesPopupSaving(true);
    setNotesError("");
    try {
      const formData = new FormData();
      formData.append("client_data", JSON.stringify({ profile: { notes_popup: next } }));
      const res = await fetch(`/v1/clients/${entriesPanelSyncClient.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "No se pudo guardar el aviso");
      }
      updateUser(entriesPanelSyncClient.id, {
        profile: { ...(entriesPanelSyncClient.profile || {}), notes_popup: next },
      });
    } catch (e) {
      setNotesError(e?.message || "Error al guardar el aviso");
    } finally {
      setNotesPopupSaving(false);
    }
  }, [entriesPanelSyncClient, token, notesPopupSaving, socioPopupOn, updateUser]);

  const saveSelectedClientNotes = useCallback(async (nextNotes) => {
    if (!entriesPanelSyncClient?.id || !token) return;
    const notesToSave = nextNotes !== undefined ? nextNotes : notesDraft;
    setNotesSaving(true);
    setNotesError("");
    try {
      const formData = new FormData();
      formData.append(
        "client_data",
        JSON.stringify({ profile: { notes: notesToSave } })
      );
      const res = await fetch(`/v1/clients/${entriesPanelSyncClient.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "No se pudieron guardar las notas");
      }
      const updated = await res.json().catch(() => null);
      if (updated?.id) {
        updateUser(updated.id, updated);
      } else {
        updateUser(entriesPanelSyncClient.id, {
          profile: { ...(entriesPanelSyncClient.profile || {}), notes: notesToSave },
        });
      }
    } catch (e) {
      setNotesError(e?.message || "Error al guardar notas");
    } finally {
      setNotesSaving(false);
    }
  }, [entriesPanelSyncClient, notesDraft, token, updateUser]);

  const handleModal = () => {
    setId(null);
    setActiveModal((v) => !v);
    getClients();
  };

  const handleBalanceModal = (userId) => {
    if (userId) {
      setUser_id(userId);
      setCreditModal(true);
    } else {
      setCreditModal(false);
    }
  };

  const onCashAdded = async (updatedUserId, newBalance) => {
    afterVenueCreditBalanceChange(updatedUserId, newBalance);
  };

  const handleNuevaEntrada = async (clientId) => {
    const client =
      nuevaResults.find((u) => u.id === clientId) ||
      clients.find((u) => u.id === clientId);
    if (!client || !venueID) return;
    try {
      await axios.post(
        "/v1/entries",
        { user_id: client.id, venue_id: venueID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const imagePath = client.profile?.image_path || client.profile?.documents_path || "";
      markUserEntered({
        ...client,
        entry_time: new Date(),
        profile: { ...client.profile, image_path: imagePath },
      });
      if (tieneAvisoSocio(client)) setAvisoSocio(client);
      resetNuevaEntradaSearch();
      setShowNuevaEntradaModal(false);
    } catch (error) {
      console.error("Error creating entry:", error);
    }
  };

  const handleAutoEntryForNewClient = async (newClient) => {
    if (!newClient || !venueID) return;
    try {
      await axios.post(
        "/v1/entries",
        { user_id: newClient.id, venue_id: venueID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const imagePath = newClient.profile?.image_path || newClient.profile?.documents_path || "";
      markUserEntered({
        ...newClient,
        entry_time: new Date(),
        profile: { ...newClient.profile, image_path: imagePath },
      });
      if (tieneAvisoSocio(newClient)) setAvisoSocio(newClient);
      await getClients();
    } catch (error) {
      console.error("Error creando entrada automática para nuevo socio:", error);
    }
  };

  const handleClientUpdated = async (updatedClient) => {
    if (!updatedClient || !updatedClient.id) return;
    if (usersShop.some((u) => u.id === updatedClient.id)) {
      const imagePath = updatedClient.profile?.image_path || updatedClient.profile?.documents_path;
      if (imagePath) updateUserImage(updatedClient.id, imagePath);
      updateUser(updatedClient.id, {
        email: updatedClient.email,
        profile: {
          ...updatedClient.profile,
          image_path: imagePath || updatedClient.profile?.image_path,
        },
      });
    }
  };

  const filteredSocios = useMemo(() => {
    const coincide = filtroBusqueda(sociosSearch);
    return usersShop.filter((u) =>
      // Regla única: se busca el mismo número que se pinta (legacy si existe,
      // interno si no) — buscar también por el interno de un migrado devolvería
      // dos personas para un número y reabre el cruce de fichas de Kong Club.
      coincide([u.profile?.first_name, u.profile?.last_name, u.email, socioNum(u)])
    );
  }, [usersShop, sociosSearch]);

  useEffect(() => {
    if (showNuevaEntradaModal) getClients();
  }, [showNuevaEntradaModal, getClients]);

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-full overflow-x-hidden">
      <CustomerModal
        active={activeModal}
        handleModal={handleModal}
        token={token}
        id={id}
        clientData={clientToEdit}
        shopName={venueName}
        shopId={venueID}
        setErrorMessage={() => {}}
        onClientCreated={handleAutoEntryForNewClient}
        onClientUpdated={handleClientUpdated}
      />
      <ClientDetailModal
        active={!!perfilClient}
        handleModal={() => setPerfilSeed(null)}
        client={perfilClient}
        token={token}
        venueID={venueID}
        isClientInVenue={perfilClient ? usersShop.some((u) => String(u.id) === String(perfilClient.id)) : false}
        onDeleted={() => getClients()}
        onEdit={() => {
          if (perfilClient) {
            setId(perfilClient.id);
            setActiveModal(true);
          }
        }}
        onAddCredit={() => {
          if (perfilClient) handleBalanceModal(perfilClient.id);
        }}
        onNotesSaved={(clientId, cambios) => updateUser(clientId, { profile: { ...cambios } })}
        onProfilePatched={(clientId, cambios) => updateUser(clientId, { profile: { ...cambios } })}
        onBalanceChanged={(clientId, newBalance) => updateUserCredit(clientId, newBalance)}
      />
      <NotaSocioPopup socio={avisoSocio} onClose={() => setAvisoSocio(null)} />
      <AddCashModal
        active={creditModal}
        handleAddCashModal={() => handleBalanceModal(null)}
        venueID={venueID}
        token={token}
        user_id={user_id}
        setErrorMessage={() => {}}
        onCashAdded={onCashAdded}
        currentBalance={usersShop.find((u) => String(u.id) === String(user_id))?.profile?.credit_balance ?? 0}
        memberName={(() => {
          const m = usersShop.find((u) => String(u.id) === String(user_id));
          const n = `${m?.profile?.first_name || ''} ${m?.profile?.last_name || ''}`.trim();
          return n || undefined;
        })()}
        memberNumber={socioNum(usersShop.find((u) => String(u.id) === String(user_id))) ?? undefined}
        memberStatus="Activo"
        memberImageUrl={(() => {
          const m = usersShop.find((u) => String(u.id) === String(user_id));
          const path = m?.profile?.documents_path || m?.profile?.image_path;
          return path ? getImageUrl(path) : undefined;
        })()}
      />

      {/* El diseño son DOS tarjetas con aire entre ellas, no una con el título
          pegado a la tabla por un borde: la cabecera manda sobre la pantalla y
          la tabla es su contenido. */}
      <div className="flex flex-col gap-4">

        {/* ── Cabecera (sticky en tablet/móvil para que Nueva entrada siga visible al hacer scroll) ── */}
        <div className="wa-entradas-header sticky top-0 z-30 flex flex-col gap-3 rounded-md border border-[var(--border)] bg-[var(--card)] p-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Jerarquía de encogido (bug 1024–1080 px, iPad Pro vertical/apaisado,
              diagnosticado 03-09): el título y «Nueva entrada» NUNCA se encogen ni
              se cortan; el que cede es el buscador. El #287 arregló <lg pero dejó
              un lg:w-64 FIJO en el input dentro de un wrapper que sí encogía: la
              fila no cabía, el wrapper encogía, el input no, y al ser `relative`
              pisaba al botón. Regla: el tope de ancho vive en el WRAPPER
              (encogible), el input siempre w-full. */}
          <div className="flex shrink-0 items-center gap-3">
            <h1 className="whitespace-nowrap text-2xl font-semibold text-[var(--foreground)]">Socios en sala</h1>
            <SectionCounter>{usersShop.length} dentro</SectionCounter>
          </div>
          <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center lg:w-auto lg:min-w-0 lg:flex-1 lg:justify-end">
            <div className="relative min-w-0 flex-1 sm:max-w-xs lg:max-w-[16rem]">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] pointer-events-none"
                size={14}
              />
              <input
                type="text"
                value={sociosSearch}
                onChange={(e) => setSociosSearch(e.target.value)}
                placeholder="Buscar socio"
                className="h-9 w-full min-w-0 rounded-md border border-[var(--border)] pl-8 pr-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--foreground)]"
              />
            </div>
            <Button className="w-full sm:w-auto" onClick={() => setShowNuevaEntradaModal(true)}>
              <LogIn size={16} strokeWidth={1.5} aria-hidden />
              Nueva entrada
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--card)]">

        {/* ── Social club: productos panel ─────────────────────── */}
        {isSocialClub && (
          <div ref={productosPanelRef} className="scroll-mt-4 border-b border-[var(--border)]">
            <Dispenser
              embedProductosInControl
              entriesPanelUserId={entriesPanelUserId}
              entriesPanelSyncClient={entriesPanelSyncClient}
              entriesPanelUsersInClub={usersShop}
              onEntriesEmbedClearClient={clearEntriesProductosPanel}
              onEmbedClientSelected={(id, socio) => selectUserForNotes(id, true, socio)}
              onEmbeddedVenueCreditSync={afterVenueCreditBalanceChange}
              onEmbeddedDraftCartSynced={fetchDraftTotals}
            />
            {/* CRUO F1: el staff manda el carrito del socio a la pantalla de Barra */}
            {entriesPanelUserId && (
              <EnviarABarra
                token={token}
                venueID={venueID}
                socioId={entriesPanelUserId}
                socioName={entriesPanelSyncClient?.profile?.first_name || null}
                onSent={() => fetchDraftTotals && fetchDraftTotals()}
              />
            )}
          </div>
        )}

        {/* ── Mobile cards (< lg) ──────────────────────────────── */}
        <div className="block lg:hidden p-3 space-y-3">
          {filteredSocios.length === 0 ? (
            <div className="py-10 text-center text-sm text-[var(--muted-foreground)]">
              No hay socios en sala.
            </div>
          ) : (
            filteredSocios.map((u) => {
              const displayName =
                u.profile?.first_name && u.profile?.last_name
                  ? `${u.profile.first_name} ${u.profile.last_name}`
                  : u.email || "Socio";
              const balance = Number(u.profile?.credit_balance ?? u.profile?.cash_balance) || 0;
              // Saldo PROYECTADO = saldo real − carrito abierto del socio (preview en vivo).
              const balanceProjected = balance - (Number(draftTotalsByUser[u.id]) || 0);
              return (
                <div
                  key={u.id}
                  className={`bg-[var(--card)] rounded-md border border-[var(--border)] p-4 transition-all ${
                    entriesPanelUserId === u.id ? "ring-2 ring-[var(--primary)]" : ""
                  }`}
                >
                  <div
                    className="flex items-center gap-3 mb-4 cursor-pointer rounded-lg p-1 -m-1 hover:bg-[var(--surface-subtle)]"
                    onClick={() => activateSocioRow(u.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        activateSocioRow(u.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <MemberPhoto
                      path={u.profile?.documents_path || u.profile?.image_path}
                      name={displayName}
                      className="w-14 h-14 rounded-md object-cover border border-[var(--border)] flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[var(--foreground)] truncate">{displayName}</span>
                        <CaducadoChip socio={u} />
                      </div>
                      <div className="text-xs text-[var(--muted-foreground)] mt-0.5">
                        Nº {socioNum(u)}
                      </div>
                      <div className="mt-1.5">
                        {/* Tres ramas escritas a mano que SÍ acertaban la regla —el cero en rojo—
                            pero con el positivo en lima-50 sobre lima-700, la marca revertida. La
                            pareja de color sale del helper; el texto se queda aquí porque el cero
                            dice «Sin saldo» y no «€0.00». */}
                        {balance !== 0 ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${balanceChipClass(balance)}`}>
                            {formatBalance(balance)}
                          </span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${balanceChipClass(balance)}`}>
                            Sin saldo
                          </span>
                        )}
                        {draftTotalsByUser[u.id] > 0 && (
                          <div className={`mt-0.5 text-[11px] ${balanceProjected < 0 ? 'text-[var(--destructive)] font-medium' : 'text-[var(--muted-foreground)]'}`}>
                            tras carrito €{balanceProjected.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs bg-[var(--surface-subtle)] rounded-lg p-3 mb-4">
                    <div className="text-[var(--muted-foreground)]">
                      Tiempo en sala
                      <div className="mt-0.5">
                        {u.entry_time ? <ElapsedCell entryTime={u.entry_time} /> : <span className="text-[var(--foreground)]">—</span>}
                      </div>
                    </div>
                    <div className="text-[var(--muted-foreground)]">
                      Estado
                      <div className="mt-0.5">
                        {u.entry_time ? <ExitStatusCell entryTime={u.entry_time} /> : <span className="text-[var(--foreground)]">—</span>}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className={isSocialClub ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
                      {!isSocialClub && (
                        <Button size="lg" className="w-full" onClick={() => navigate(`/dispenser?userId=${u.id}`)}>
                          <GiHemp className="text-base" />
                          <span>Dispensario</span>
                        </Button>
                      )}
                      <Button
                        size="lg"
                        className="w-full"
                        onClick={() =>
                          isSocialClub
                            ? goToProductosOnControlWithRefresh(u.id)
                            : navigate(`/dispenser?mode=productos&userId=${u.id}`)
                        }
                      >
                        <MdLocalDrink className="text-base" />
                        <span>Productos</span>
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" size="lg" className="w-full" onClick={() => handleBalanceModal(u.id)}>
                        <MdCreditCard className="text-base" />
                        <span>Crédito</span>
                      </Button>
                      <Button variant="outline" size="lg" className="w-full" onClick={() => navigate(`/carritos?user_id=${u.id}`)}>
                        <HiOutlineShoppingCart className="text-base" />
                        <span>Carrito</span>
                      </Button>
                    </div>
                    <Button variant="outline" size="lg" className="w-full" onClick={() => openSocioPerfil(u.id)}>
                      <CircleUserRound className="text-base" size={16} />
                      <span>Perfil</span>
                    </Button>
                    {/* «Salir» conserva el rojo suave (fondo destructive-bg, texto
                        destructive) que ya llevaba: destructivo pero no gritón en
                        cada tarjeta. El sistema no tiene esa variante, así que va a
                        mano con sus tokens. */}
                    <button
                      onClick={() => handleCheckout(u.id)}
                      className="w-full inline-flex h-10 items-center justify-center gap-2 px-3 bg-[var(--destructive-bg)] hover:opacity-[0.88] text-[var(--destructive)] rounded-sm text-sm font-medium transition-opacity"
                    >
                      <GrLogout className="text-base" />
                      <span>Salir</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Desktop table (≥ lg) ─────────────────────────────── */}
        <div className="hidden lg:block">
          <table className="min-w-full text-sm">
            <TableHead>
              <Th>Socio</Th>
              <Th>Saldo</Th>
              <Th>Tiempo en sala</Th>
              <Th>Estado de salida</Th>
              <Th align="right">Acciones</Th>
            </TableHead>
            <tbody>
              {filteredSocios.map((u) => {
                const balance = Number(u.profile?.credit_balance ?? u.profile?.cash_balance) || 0;
                // Saldo PROYECTADO = saldo real − carrito abierto del socio (preview en vivo).
                const balanceProjected = balance - (Number(draftTotalsByUser[u.id]) || 0);
                return (
                  <Tr
                    key={u.id}
                    className={entriesPanelUserId === u.id ? "!bg-[var(--muted)]" : ""}
                  >
                    {/* Socio */}
                    <Td
                      className="cursor-pointer"
                      onClick={() => activateSocioRow(u.id)}
                    >
                      <div className="flex items-center gap-3">
                        <MemberPhoto
                          path={u.profile?.documents_path || u.profile?.image_path}
                          name={`${u.profile?.first_name || ''} ${u.profile?.last_name || ''}`.trim()}
                          className={FOTO_DE_FILA}
                        />
                        <div>
                          <div className="flex items-center gap-2 font-medium text-[var(--foreground)]">
                            <span>{u.profile?.first_name} {u.profile?.last_name}</span>
                            <CaducadoChip socio={u} />
                          </div>
                          <div className="text-xs text-[var(--muted-foreground)]">
                            Nº {socioNum(u)}
                          </div>
                        </div>
                      </div>
                    </Td>

                    {/* Saldo (real) + efecto del carrito como línea secundaria */}
                    <Td>
                      {/* Tres ramas escritas a mano que SÍ acertaban la regla —el cero en rojo—
                          pero con el positivo en lima-50 sobre lima-700, la marca revertida. La
                          pareja de color sale del helper; el texto se queda aquí porque el cero
                          dice «Sin saldo» y no «€0.00». */}
                      {balance !== 0 ? (
                        <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-medium ${balanceChipClass(balance)}`}>
                          {formatBalance(balance)}
                        </span>
                      ) : (
                        <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-medium ${balanceChipClass(balance)}`}>
                          Sin saldo
                        </span>
                      )}
                      {draftTotalsByUser[u.id] > 0 && (
                        <div className={`mt-0.5 text-[11px] ${balanceProjected < 0 ? 'text-[var(--destructive)] font-medium' : 'text-[var(--muted-foreground)]'}`}>
                          tras carrito €{balanceProjected.toFixed(2)}
                        </div>
                      )}
                    </Td>

                    {/* Tiempo en sala */}
                    <Td>
                      {u.entry_time
                        ? <ElapsedCell entryTime={u.entry_time} />
                        : <span className="text-[var(--muted-foreground)]">—</span>}
                    </Td>

                    {/* Estado de salida */}
                    <Td>
                      {u.entry_time
                        ? <ExitStatusCell entryTime={u.entry_time} />
                        : <span className="text-[var(--muted-foreground)]">—</span>}
                    </Td>

                    {/* Acciones */}
                    <Td align="right">
                      <button
                        onClick={(e) => openActionsMenu(e, u)}
                        className="p-1.5 rounded-md border border-[var(--border)] hover:bg-[var(--muted)] transition-colors text-[var(--foreground)]"
                        title="Acciones"
                        aria-haspopup="menu"
                        aria-expanded={actionMenu?.user.id === u.id}
                      >
                        <EllipsisVertical size={16} />
                      </button>
                    </Td>
                  </Tr>
                );
              })}
              {filteredSocios.length === 0 && (
                <tr>
                  <Td align="center" colSpan={5} className="py-10 text-sm text-[var(--muted-foreground)]">
                    No hay socios en sala.
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {/* Menú ⋮ de fila: MenuFlotante (portal al body, fijo a la ventana, flip con
          la altura medida). Antes un `fixed z-50` a mano midiendo el botón
          y una altura estimada (auditoría de UI, UI-009). */}
      {actionMenu && (() => {
        const u = actionMenu.user;
        const hasDraftCart = draftTotalsByUser[u.id] > 0;
        const cerrarY = (fn) => () => { setActionMenu(null); fn(); };
        return (
          <MenuFlotante anchorEl={actionMenu.anchorEl} onClose={closeActionMenu} className="min-w-[192px]">
            {!isSocialClub && (
              <button type="button" role="menuitem" className={itemMenu} onClick={cerrarY(() => navigate(`/dispenser?userId=${u.id}`))}>
                <GiHemp size={14} />
                Dispensario
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={itemMenu}
              onClick={cerrarY(() => (isSocialClub ? goToProductosOnControlWithRefresh(u.id) : navigate(`/dispenser?mode=productos&userId=${u.id}`)))}
            >
              <MdLocalDrink size={14} />
              Barra
            </button>
            <button type="button" role="menuitem" className={itemMenu} onClick={cerrarY(() => handleBalanceModal(u.id))}>
              <MdCreditCard size={14} />
              Saldo
              {hasDraftCart && (
                <span className="ml-auto text-xs text-[var(--muted-foreground)]">
                  €{Number(draftTotalsByUser[u.id]).toFixed(2)}
                </span>
              )}
            </button>
            <button type="button" role="menuitem" className={itemMenu} onClick={cerrarY(() => navigate(`/carritos?user_id=${u.id}`))}>
              <HiOutlineShoppingCart size={14} />
              Carrito
            </button>
            <div className="my-1 border-t border-[var(--border)]" />
            <button type="button" role="menuitem" className={itemMenu} onClick={cerrarY(() => openSocioPerfil(u.id))}>
              <CircleUserRound size={14} />
              Perfil
            </button>
            <button type="button" role="menuitem" className={itemMenu} onClick={cerrarY(() => { selectUserForNotes(u.id); setNotesPanelOpen(true); })}>
              <HiOutlinePencilSquare size={14} />
              Notas
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex h-8 w-full items-center gap-2.5 rounded-sm px-3 text-sm text-[var(--destructive)] transition-colors hover:bg-[var(--destructive-bg)]"
              onClick={cerrarY(() => handleCheckout(u.id))}
            >
              <GrLogout size={14} />
              Salida
            </button>
          </MenuFlotante>
        );
      })()}

      {/* ── Notes panel (mismo diseño; visible también en tablet vertical < lg) ──
          El envoltorio mide lo que mide el panel ABIERTO aunque esté cerrado y solo
          asome la pestaña, y un div recibe el puntero por su caja, no por lo que
          pinta: `pointer-events-none` aquí y `auto` en pestaña y panel, o la caja
          vacía se traga lo que tenga debajo (a 844×390 llegaba al avatar del menú
          de usuario, UI-029). En apaisado corto, además, cuelga por debajo de la
          cabecera en vez de centrarse: la posición manda sobre el z-index. En
          vertical (móvil e iPad) cuelga abajo a la derecha: centrada a 42 % pisaba
          el buscador y «Nueva entrada» a 390 (UI-015). Candado: geometria-capas.spec.js. */}
      <div className="block">
        <div
          className={`pointer-events-none fixed right-0 top-1/2 z-40 -translate-y-1/2 transition-transform duration-300 max-lg:top-[42%] max-lg:landscape:top-16 max-lg:landscape:translate-y-0 max-lg:portrait:top-auto max-lg:portrait:bottom-24 max-lg:portrait:translate-y-0 ${
            notesPanelOpen ? "translate-x-0" : "translate-x-[calc(100%-56px)]"
          }`}
        >
          <div className="flex items-start">
            <button
              type="button"
              onClick={() => setNotesPanelOpen((v) => !v)}
              className="pointer-events-auto mt-12 flex h-20 w-12 flex-col items-center justify-center gap-1 rounded-l-md border border-[var(--primary)] bg-[var(--primary)] px-1 text-center text-[10px] font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-[0.88] max-lg:mt-8 lg:mt-20"
              title={notesPanelOpen ? "Ocultar notas" : "Mostrar notas"}
            >
              <HiOutlinePencilSquare className="text-lg" />
              <span>Notas</span>
            </button>

            <aside className="pointer-events-auto w-[min(360px,calc(100vw-3.5rem))] max-h-[min(520px,calc(100dvh-5rem))] max-lg:landscape:max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-l-md border border-[var(--border)] bg-[var(--card)]">
              <div className="flex items-start justify-between gap-3 border-b border-[var(--border)] bg-[var(--muted)] px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--foreground)]">Cuadro de notas</h2>
                  <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                    {entriesPanelSyncClient
                      ? selectedClientName
                      : "Identifica primero un socio para ver su cuadro de notas."}
                  </p>
                </div>
                {entriesPanelSyncClient ? (
                  <button
                    type="button"
                    onClick={() => {
                      setId(entriesPanelSyncClient.id);
                      setActiveModal(true);
                    }}
                    className="rounded-sm border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm font-medium text-[var(--foreground)] transition hover:border-[var(--border-hover)]"
                  >
                    Editar
                  </button>
                ) : null}
              </div>

              <div className="p-4">
                <label className="mb-4 flex items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--background)] px-4 py-3 text-sm text-[var(--foreground)]">
                  <button
                    type="button"
                    onClick={toggleNotesPopup}
                    disabled={!entriesPanelSyncClient || notesPopupSaving}
                    className={`relative h-7 w-12 rounded-full transition disabled:opacity-40 ${socioPopupOn ? "bg-[var(--primary)]" : "bg-[var(--border)]"}`}
                    aria-pressed={socioPopupOn}
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${socioPopupOn ? "left-6" : "left-1"}`}
                    />
                  </button>
                  <span>
                    Abrir al interactuar con el socio
                    <span className="block text-xs text-[var(--muted-foreground)]">
                      Se guarda para este socio y lo ve todo el equipo
                    </span>
                  </span>
                </label>

                <div className="min-h-[240px] max-lg:min-h-[200px] lg:min-h-[360px] rounded-lg border border-[var(--border)] bg-[var(--background)] p-4 flex flex-col gap-3">
                  {notesError ? (
                    <div className="rounded-md border border-[var(--destructive-border)] bg-[var(--destructive-bg)] px-3 py-2 text-xs text-[var(--destructive)]">
                      {notesError}
                    </div>
                  ) : null}
                  <textarea
                    value={entriesPanelSyncClient ? notesDraft : ""}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    disabled={!entriesPanelSyncClient || notesSaving}
                    className={`flex-1 w-full resize-none rounded-md border px-3 py-2 text-sm leading-6 outline-none ${
                      entriesPanelSyncClient
                        ? "border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] focus:ring-2 focus:ring-[var(--ring-selected)]"
                        : "border-[var(--border)] bg-[var(--muted)] text-[var(--muted-foreground)]"
                    }`}
                    placeholder={
                      entriesPanelSyncClient
                        ? "Escribe notas internas sobre este socio…"
                        : "Selecciona un socio desde la tabla para ver su cuadro de notas."
                    }
                  />
                  <button
                    type="button"
                    onClick={() => saveSelectedClientNotes()}
                    disabled={!entriesPanelSyncClient || notesSaving}
                    className="w-full rounded-sm bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-[0.88] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {notesSaving ? "Guardando…" : "Guardar notas"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNotesDraft("");
                      setNotesError("");
                      saveSelectedClientNotes("");
                    }}
                    disabled={!entriesPanelSyncClient || notesSaving}
                    className="w-full rounded-sm border border-[var(--destructive-border)] bg-[var(--background)] px-4 py-2 text-sm font-medium text-[var(--destructive)] transition-colors hover:bg-[var(--destructive-bg)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Borrar notas
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* ── Nueva entrada: el CustomModal del sistema (Figma), con role="dialog",
          foco y cierre por la ✕ o el telón (Escape no, mientras el foco esté en el
          buscador: CustomModal lo ignora dentro de un campo). Antes un telón `fixed inset-0 z-50` y un
          panel con sombra inline y radio 10 hechos a mano (auditoría de UI, UI-009). */}
      <CustomModal
        active={showNuevaEntradaModal}
        handleModal={() => {
          setShowNuevaEntradaModal(false);
          resetNuevaEntradaSearch();
        }}
        title="Nueva entrada"
        size="sm"
        titleId="nueva-entrada-title"
      >
        {showNuevaEntradaModal && (
          <>
            {/* Search input */}
            <div className="relative mb-4">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] pointer-events-none"
                size={16}
              />
              <input
                type="text"
                value={searchFieldNueva}
                onChange={handleNuevaSearchChange}
                placeholder="Buscar socio"
                className="w-full h-9 rounded-md border border-[var(--border)] pl-9 pr-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--ring-selected)]"
                autoFocus
              />
            </div>

            {/* Results */}
            <div>
              {!searchFieldNueva.trim() ? (
                <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
                  Empieza a escribir para buscar…
                </p>
              ) : nuevaSearching ? (
                <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">Buscando…</p>
              ) : nuevaResults.filter((c) => !usersShop.some((u) => u.id === c.id)).length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--muted)]">
                    <SearchX size={16} className="text-[var(--muted-foreground)]" />
                  </span>
                  <p className="text-sm font-medium text-[var(--foreground)]">Sin resultados</p>
                  <p className="text-xs text-[var(--muted-foreground)]">No hay socios que coincidan con tu búsqueda</p>
                </div>
              ) : (
                <div>
                  {nuevaResults
                    .filter((c) => !usersShop.some((u) => u.id === c.id))
                    .map((c) => {
                      const balance = Number(c.profile?.credit_balance ?? c.profile?.cash_balance) || 0;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleNuevaEntrada(c.id)}
                          className="flex w-full items-center gap-3 py-2 border-b border-[var(--border)] last:border-b-0 text-left transition-colors hover:bg-[var(--surface-subtle)]"
                        >
                          <MemberPhoto
                            path={c.profile?.documents_path || c.profile?.image_path}
                            name={`${c.profile?.first_name || ''} ${c.profile?.last_name || ''}`.trim()}
                            className="w-[46px] h-[46px] rounded-lg object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium text-[var(--foreground)]">
                              <span className="truncate">{c.profile?.first_name} {c.profile?.last_name}</span>
                              <CaducadoChip socio={c} />
                            </div>
                            <div className="text-sm text-[var(--muted-foreground)] mt-0.5">
                              Nº Socio: {socioNum(c)}
                              {" · "}
                              Crédito: <span className={balanceToneClass(balance, "text-[var(--foreground)]")}>{formatBalance(balance)}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </>
        )}
      </CustomModal>
      {dialogoConfirmar}
    </div>
  );
}
